import Unsplash from 'unsplash-js';
import kebabCase from 'lodash.kebabcase';

import banzai from 'ub/control/banzai-features';
import score from 'ub/thirdparty/quicksilver';
import endpoints from 'ub/config/endpoints';
import { getType } from 'ui/asset-library/asset-types';
import { uploadFiles$ } from 'ub/control/asset-upload';

const handleResponse = response => {
  if (response.ok) {
    return response.json();
  } else {
    throw new Error(`${response.status} ${response.statusText}`);
  }
};

const fetchItemsFromClient = clientId =>
  fetch(endpoints.assetsByCompanyId(clientId), {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
    },
  })
    .then(handleResponse)
    .then(assets => ({
      totalPages: 1, // The webapp does not currently support pagination of assets
      fetchedItems: assets.map(asset => ({
        asset,
        filename: asset.content_file_name,
        fullUrl: asset.content_url,
        id: asset.uuid,
        thumbnailUrl: asset.content_url_small,
        type: getType(asset.content_content_type),
      })),
    }));

const deleteItemsFromClient = (clientId, assetIntegerIds, authToken) =>
  fetch(endpoints.deleteAssets(clientId), {
    body: assetIntegerIds.map(assetId => `assets[]=${assetId}`).join('&'),
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-CSRF-Token': authToken,
    },
    method: 'POST',
  }).then(handleResponse);

const uploadFilesToClient = (clientId, files, authToken, onlyImages) =>
  uploadFiles$(files, authToken, onlyImages).map(result => {
    if (result.error) {
      return result;
    } else {
      return {
        error: null,
        item: {
          asset: result.asset,
          filename: result.asset.content_file_name,
          fullUrl: result.asset.content_url,
          id: result.asset.uuid,
          thumbnailUrl: result.asset.content_url_small,
          type: getType(result.asset.content_content_type),
        },
      };
    }
  });

const filterByKeyword = (items, searchTerm) =>
  items
    .map(item => ({
      item,
      score: score(item.asset.content_file_name, searchTerm),
    }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);

const unsplash = new Unsplash({ applicationId: banzai.getFeatureValue('unsplashAccessKey') });

export default {
  currentClient: {
    deleteItems: (...args) => deleteItemsFromClient(window.editor.currentCompanyId, ...args),
    enabled: true,
    fetch: () => fetchItemsFromClient(window.editor.currentCompanyId),
    filterByKeyword,
    label: 'My Images',
    upload: (...args) => uploadFilesToClient(window.editor.currentCompanyId, ...args),
  },

  unsplash: {
    enabled: banzai.features.isUnsplashEnabled(),
    fetchByKeyword: (keyword, page) =>
      unsplash.search
        .photos(keyword, page, 30) // 30 seems to be the maximum results per page
        .then(handleResponse)
        .then(response => ({
          totalPages: response.total_pages,
          fetchedItems: response.results.map(photo => ({
            filename: `unsplash-${photo.slug ||
              kebabCase(photo.description || '').substr(0, 50) ||
              photo.id}.jpg`,
            fullUrl: photo.urls.full, // photo.urls.raw is also available but the file sizes are huge
            id: photo.id,
            thumbnailUrl: photo.urls.small,
            type: 'images',
            unsplash: {
              authorLink: photo.user.links.html,
              authorName: photo.user.name,
              description: photo.description || '',
              link: photo.links.html,
            },
          })),
        })),
    isExternal: true,
    isNew: true, // Show 'New!' flair
    label: 'Unsplash Photos',
    onChoose: items =>
      // A requirement of Unsplash's API is that we make a request to the download endpoint when the
      // user performs a download-like action. onChoose fires when the user selects the image to be
      // copied to their library. This is fire-and-forget – we don't care about the response or
      // whether it succeeded or failed.
      // https://medium.com/unsplash/unsplash-api-guidelines-triggering-a-download-c39b24e99e02
      Promise.all(
        items.map(item =>
          unsplash.photos
            .getPhoto(item.id)
            .then(handleResponse)
            .then(unsplash.photos.downloadPhoto)
        )
      ),
  },

  publicImages: {
    enabled: true,
    fetch: () => fetchItemsFromClient(window.editor.unbounceAssetsCompanyId),
    label: 'Unbounce Public Images',
    filterByKeyword,
  },
};



// WEBPACK FOOTER //
// ./src/ui/asset-library/asset-sources.js